/**
 * Chunk Queue Manager
 *
 * 청크를 순서대로 관리하고 소비자에게 전달하는 순수 TypeScript 클래스
 *
 * 책임:
 * - 청크 순서 보장 (sequence_num 기반)
 * - 턴 ID별 청크 관리
 * - 소비자 패턴 (Observer 패턴)
 * - 메모리 효율적 관리 (소비된 청크 제거)
 */

export type ChunkConsumer<T> = (chunk: T | "END") => void;

export interface ChunkQueueConfig {
  maxQueueSize?: number; // 최대 큐 크기 (메모리 보호)
  logEnabled?: boolean;  // 로그 활성화
}

export class ChunkQueueManager<T extends { sequence_num: number; turn_id: string }> {
  private queue: T[] = [];
  private consumers: Set<ChunkConsumer<T>> = new Set();
  private consumerSubscriptions = new Map<ChunkConsumer<T>, number>();
  private nextSubscriptionId: number = 0;
  private currentTurnId: string | null = null;
  private hasReceivedLastChunk: boolean = false;
  private nextExpectedSequence: number = 0;
  private isWaitingForNextChunk: boolean = false;
  private hasDeliveredEnd: boolean = false;
  private deliveryGeneration: number = 0;
  private config: Required<ChunkQueueConfig>;

  constructor(config: ChunkQueueConfig = {}) {
    this.config = {
      maxQueueSize: config.maxQueueSize ?? 100,
      logEnabled: config.logEnabled ?? false,
    };
  }

  /**
   * 소비자 등록
   */
  subscribe(consumer: ChunkConsumer<T>): () => void {
    const subscriptionId = ++this.nextSubscriptionId;
    this.consumers.add(consumer);
    this.consumerSubscriptions.set(consumer, subscriptionId);
    this.log(`📝 소비자 등록됨. 총 ${this.consumers.size}명`);

    // 구독 시 대기 중인 첫 번째 청크(seq=0)가 있으면 즉시 전달
    if (
      this.queue.length > 0 &&
      this.queue[0].sequence_num === 0 &&
      this.nextExpectedSequence === 0
    ) {
      this.log(`📤 구독 시 대기 중인 첫 번째 청크 전달 예약`);
      this.scheduleConsumerDelivery(consumer, subscriptionId);
    } else if (
      this.queue.length === 1 &&
      this.queue[0].sequence_num === -1 &&
      this.nextExpectedSequence === 0
    ) {
      this.log(`📤 구독 시 대기 중인 오디오 없는 턴의 END 전달`);
      this.scheduleConsumerDelivery(consumer, subscriptionId);
    } else if (
      this.queue.length === 0 &&
      this.hasReceivedLastChunk &&
      !this.hasDeliveredEnd
    ) {
      this.scheduleConsumerDelivery(consumer, subscriptionId);
    } else if (
      this.hasReceivedLastChunk &&
      this.nextExpectedSequence === 0 &&
      this.queue.some((chunk) => chunk.sequence_num >= 0)
    ) {
      // END proves that any lower missing sequence will never arrive. Start
      // from the first successful audio chunk rather than stalling forever.
      this.scheduleConsumerDelivery(consumer, subscriptionId);
    }

    // unsubscribe 함수 반환
    return () => {
      if (this.consumerSubscriptions.get(consumer) !== subscriptionId) return;
      this.consumerSubscriptions.delete(consumer);
      this.consumers.delete(consumer);
      this.log(`📝 소비자 해제됨. 남은 ${this.consumers.size}명`);
    };
  }

  /**
   * 새로운 턴 시작
   */
  startNewTurn(turnId: string): void {
    this.log(`🔄 새 턴 시작: ${turnId}`);

    // 이전 턴 정리
    this.clear();

    this.currentTurnId = turnId;
    this.hasReceivedLastChunk = false;
    this.hasDeliveredEnd = false;
    this.nextExpectedSequence = 0;
  }

  /**
   * 청크 추가
   */
  enqueue(chunk: T): boolean {
    // 턴 ID 검증
    if (!this.currentTurnId || chunk.turn_id !== this.currentTurnId) {
      this.log(`🚫 턴 ID 불일치로 청크 무시: current=${this.currentTurnId}, received=${chunk.turn_id}`);
      return false;
    }

    // 이미 마지막 청크를 받았으면 더 이상 추가하지 않음
    if (this.hasReceivedLastChunk) {
      this.log(`🚫 이미 마지막 청크를 받았으므로 무시: seq=${chunk.sequence_num}`);
      return false;
    }

    // 큐 크기 체크
    if (this.queue.length >= this.config.maxQueueSize) {
      console.warn(`⚠️ 큐가 가득 참: ${this.queue.length}/${this.config.maxQueueSize}`);
      return false;
    }

    // 마지막 청크 감지 (-1)
    if (chunk.sequence_num === -1) {
      this.log(`🏁 마지막 청크 도착 (현재 큐 크기: ${this.queue.length})`);
      this.hasReceivedLastChunk = true;

      // ⚠️ 큐가 비어있어도 즉시 END를 보내지 않음!
      // 이유: 현재 재생 중인 청크가 있을 수 있음
      // END 신호는 다음 tryConsumeNext()에서 자동으로 전송됨
    }

    // 중복 청크 체크 (같은 sequence_num이 이미 있으면 무시)
    const isDuplicate = this.queue.some(
      (existingChunk) => existingChunk.sequence_num === chunk.sequence_num
    );

    if (isDuplicate) {
      this.log(`🚫 중복 청크 무시: seq=${chunk.sequence_num}`);
      return false;
    }

    // 큐에 추가
    this.queue.push(chunk);
    this.log(`➕ 청크 추가: seq=${chunk.sequence_num}, 큐 크기=${this.queue.length}`);

    // 정렬 (sequence_num 기준)
    this.queue.sort((a, b) => {
      // -1은 항상 마지막
      if (a.sequence_num === -1) return 1;
      if (b.sequence_num === -1) return -1;
      return a.sequence_num - b.sequence_num;
    });

    // ✅ 첫 번째 청크(seq=0) 자동 전달
    // 소비자가 등록되어 있고, 첫 번째 청크가 도착하면 즉시 전달
    if (
      this.queue.length > 0 &&
      this.queue[0].sequence_num === 0 &&
      this.nextExpectedSequence === 0 &&
      this.consumers.size > 0
    ) {
      const firstChunk = this.queue.shift()!;
      this.nextExpectedSequence = 1;
      this.isWaitingForNextChunk = false;
      this.notifyConsumers(firstChunk);
      this.log(`📤 첫 번째 청크 자동 전달: seq=${firstChunk.sequence_num}`);
    }

    // 오디오가 하나도 생성되지 않은 턴은 첫 메시지가 곧 terminal(-1)이다.
    // 재생 완료 콜백이 없으므로 즉시 END를 전달해 로딩/인사 상태를 정리한다.
    if (
      this.queue.length === 1 &&
      this.queue[0].sequence_num === -1 &&
      this.nextExpectedSequence === 0 &&
      this.consumers.size > 0
    ) {
      this.queue.shift();
      this.isWaitingForNextChunk = false;
      this.deliverEnd();
      this.log(`📤 오디오 없는 턴의 END 즉시 전달`);
    }

    // A terminal marker makes sequence gaps permanent. If playback has not
    // started, begin with the first chunk that actually succeeded.
    if (
      chunk.sequence_num === -1 &&
      this.nextExpectedSequence === 0 &&
      this.consumers.size > 0 &&
      this.queue.some((queuedChunk) => queuedChunk.sequence_num >= 0)
    ) {
      const firstSuccessfulChunk = this.shiftFirstSuccessfulChunk();
      if (firstSuccessfulChunk) {
        this.nextExpectedSequence = firstSuccessfulChunk.sequence_num + 1;
        this.isWaitingForNextChunk = false;
        this.notifyConsumers(firstSuccessfulChunk);
      }
    }

    // 이전 청크가 끝난 뒤 큐가 비어 소비자가 기다리는 중이었다면,
    // 기대하던 순번이 도착하는 즉시 재생 체인을 다시 깨운다.
    if (
      this.isWaitingForNextChunk &&
      this.queue.length > 0 &&
      this.queue[0].sequence_num === -1 &&
      this.consumers.size > 0
    ) {
      this.queue.shift();
      this.isWaitingForNextChunk = false;
      this.deliverEnd();
      this.log(`📤 대기 중인 소비자에게 END 전달`);
    } else if (
      this.isWaitingForNextChunk &&
      this.queue.length > 0 &&
      this.queue[0].sequence_num === this.nextExpectedSequence &&
      this.consumers.size > 0
    ) {
      const waitingChunk = this.queue.shift()!;
      this.nextExpectedSequence++;
      this.isWaitingForNextChunk = false;
      this.notifyConsumers(waitingChunk);
      this.log(`📤 대기 중인 소비자에게 청크 전달: seq=${waitingChunk.sequence_num}`);
    } else if (
      this.isWaitingForNextChunk &&
      this.hasReceivedLastChunk &&
      this.queue.some((queuedChunk) => queuedChunk.sequence_num >= 0) &&
      this.consumers.size > 0
    ) {
      const waitingChunk = this.shiftFirstSuccessfulChunk();
      if (waitingChunk) {
        this.nextExpectedSequence = waitingChunk.sequence_num + 1;
        this.isWaitingForNextChunk = false;
        this.notifyConsumers(waitingChunk);
        this.log(`📤 terminal gap 건너뛰고 청크 전달: seq=${waitingChunk.sequence_num}`);
      }
    }

    return true;
  }

  /**
   * 다음 청크 소비 시도 (자동 전달용)
   */
  private tryConsumeNext(): void {
    while (this.queue.length > 0) {
      const nextChunk = this.queue[0];

      // 마지막 청크가 아니고, 순서가 맞지 않으면 대기
      if (nextChunk.sequence_num !== -1 && nextChunk.sequence_num !== this.nextExpectedSequence) {
        this.log(`⏸️ 순서 대기: expected=${this.nextExpectedSequence}, got=${nextChunk.sequence_num}`);
        break;
      }

      // 큐에서 제거
      this.queue.shift();

      // 마지막 청크인 경우
      if (nextChunk.sequence_num === -1) {
        this.log(`📤 마지막 청크 전달 및 END 신호`);
        this.deliverEnd();
        break;
      }

      // 일반 청크 소비
      this.log(`📤 청크 소비: seq=${nextChunk.sequence_num}`);
      this.notifyConsumers(nextChunk);
      this.nextExpectedSequence++;
    }
  }

  /**
   * 다음 청크를 수동으로 꺼내기 (동기 방식)
   *
   * 오디오 재생이 끝났을 때 명시적으로 다음 청크를 요청할 때 사용
   */
  dequeue(): T | "END" | null {
    // 큐가 비어있으면
    if (this.queue.length === 0) {
      // 마지막 청크를 이미 받았으면 END 반환
      if (this.hasReceivedLastChunk && !this.hasDeliveredEnd) {
        this.hasDeliveredEnd = true;
        this.log(`🏁 큐가 비었고 마지막 청크 수신됨 → END`);
        return "END";
      }
      // 아직 청크가 더 올 예정이면 null 반환
      this.isWaitingForNextChunk = true;
      this.log(`⏳ 큐가 비어있음 - 다음 청크 대기`);
      return null;
    }

    const nextChunk = this.queue[0];

    // 순서가 맞지 않으면 대기
    if (
      nextChunk.sequence_num !== -1 &&
      nextChunk.sequence_num !== this.nextExpectedSequence &&
      !this.hasReceivedLastChunk
    ) {
      this.isWaitingForNextChunk = true;
      this.log(`⏸️ 순서 불일치로 대기: expected=${this.nextExpectedSequence}, got=${nextChunk.sequence_num}`);
      return null;
    }

    // 큐에서 제거
    this.queue.shift();

    // 마지막 청크인 경우
    if (nextChunk.sequence_num === -1) {
      this.isWaitingForNextChunk = false;
      if (this.hasDeliveredEnd) return null;
      this.hasDeliveredEnd = true;
      this.log(`📤 마지막 청크 수동 추출 → END 반환 예정`);
      return "END";
    }

    // 일반 청크
    this.log(`📤 청크 수동 추출: seq=${nextChunk.sequence_num}`);
    this.nextExpectedSequence = nextChunk.sequence_num + 1;
    this.isWaitingForNextChunk = false;
    return nextChunk;
  }

  /**
   * 소비자들에게 알림
   */
  private notifyConsumers(chunk: T | "END"): void {
    this.consumers.forEach((consumer) => {
      try {
        consumer(chunk);
      } catch (error) {
        console.error("❌ 청크 소비 중 에러:", error);
      }
    });
  }

  private deliverEnd(): void {
    if (this.hasDeliveredEnd) return;
    this.hasDeliveredEnd = true;
    this.notifyConsumers("END");
  }

  private scheduleConsumerDelivery(consumer: ChunkConsumer<T>, subscriptionId: number): void {
    const generation = this.deliveryGeneration;
    setTimeout(() => {
      if (
        generation === this.deliveryGeneration &&
        this.consumers.has(consumer) &&
        this.consumerSubscriptions.get(consumer) === subscriptionId
      ) {
        this.deliverAvailableToSubscriber(consumer);
      }
    }, 0);
  }

  /**
   * Consume only after the delayed subscription callback is still proven
   * current. This keeps unsubscribe/new-turn invalidation from losing a chunk
   * that was removed from the queue before the callback ran.
   */
  private deliverAvailableToSubscriber(consumer: ChunkConsumer<T>): void {
    if (
      this.queue.length > 0 &&
      this.queue[0].sequence_num === 0 &&
      this.nextExpectedSequence === 0
    ) {
      const firstChunk = this.queue.shift()!;
      this.nextExpectedSequence = 1;
      this.isWaitingForNextChunk = false;
      consumer(firstChunk);
      return;
    }

    if (
      this.queue.length === 1 &&
      this.queue[0].sequence_num === -1 &&
      this.nextExpectedSequence === 0
    ) {
      this.queue.shift();
      this.isWaitingForNextChunk = false;
      if (!this.hasDeliveredEnd) {
        this.hasDeliveredEnd = true;
        consumer("END");
      }
      return;
    }

    if (this.queue.length === 0 && this.hasReceivedLastChunk && !this.hasDeliveredEnd) {
      this.hasDeliveredEnd = true;
      consumer("END");
      return;
    }

    if (
      this.hasReceivedLastChunk &&
      this.nextExpectedSequence === 0 &&
      this.queue.some((chunk) => chunk.sequence_num >= 0)
    ) {
      const firstSuccessfulChunk = this.shiftFirstSuccessfulChunk();
      if (firstSuccessfulChunk) {
        this.nextExpectedSequence = firstSuccessfulChunk.sequence_num + 1;
        this.isWaitingForNextChunk = false;
        consumer(firstSuccessfulChunk);
      }
    }
  }

  private shiftFirstSuccessfulChunk(): T | null {
    const index = this.queue.findIndex((chunk) => chunk.sequence_num >= 0);
    if (index < 0) return null;
    const [chunk] = this.queue.splice(index, 1);
    return chunk ?? null;
  }

  /**
   * 큐 클리어
   */
  clear(): void {
    this.deliveryGeneration++;
    this.queue = [];
    this.currentTurnId = null;
    this.hasReceivedLastChunk = false;
    this.hasDeliveredEnd = false;
    this.nextExpectedSequence = 0;
    this.isWaitingForNextChunk = false;
    this.log(`🗑️ 큐 클리어됨`);
  }

  /**
   * 현재 상태 조회
   */
  getState() {
    return {
      queueSize: this.queue.length,
      currentTurnId: this.currentTurnId,
      hasReceivedLastChunk: this.hasReceivedLastChunk,
      hasDeliveredEnd: this.hasDeliveredEnd,
      nextExpectedSequence: this.nextExpectedSequence,
      isWaitingForNextChunk: this.isWaitingForNextChunk,
      consumerCount: this.consumers.size,
    };
  }

  /**
   * 로그 출력
   */
  private log(message: string): void {
    if (this.config.logEnabled) {
      console.log(`[ChunkQueue] ${message}`);
    }
  }

  /**
   * 리소스 정리
   */
  destroy(): void {
    this.clear();
    this.consumers.clear();
    this.consumerSubscriptions.clear();
    this.log(`💥 Queue Manager 파괴됨`);
  }
}
